import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Message } from '@iwoca/orion';
import { Trans } from '@lingui/react/macro';
import { useIntroducerData } from 'contexts/IntroducerData/IntroducerData.context';
import { useAmplitude } from 'hooks/useAmplitude';
import { FormProvider, FormState, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { IwQueryClient } from 'store/IwQueryClient';
import { TImperativeHandleSignup } from 'types';
import { getCookie } from 'utils/cookieHelper';
import { PATH_NAMES } from 'utils/routes/pathNames';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import { DevTimeSaver } from '../DevTimeSaver/DevTimeSaver';
import { useSignupDataDE } from '../hooks/useSignupDataDE/useSignupDataDE';
import { LoanDetailsDE } from '../LoanDetailsDE/LoanDetailsDE';
import { PersonalInformationDE } from '../PersonalInformationDE/PersonalInformationDE';
import { CompanyDetailsDE } from './CompanyDetailsDE/CompanyDetailsDE';
import { useHandleSubmitError } from './helpers/SubmitErrorHelper';
import styles from './NewSignupFormDE.module.css';
import { schemaDE } from './NewSignUpFormDE.schema';
import { TSignupFormDataDE } from './NewSignupFormDE.types';
import { TermsAndConditions } from './TermsAndConditions/TermsAndConditions';

type TProps = {
  setSaveForLaterDisabled?: (data: boolean) => void;
  setSaveForLaterLoading?: (data: boolean) => void;
};

export const NewSignupFormDE = forwardRef<TImperativeHandleSignup, TProps>(
  ({ setSaveForLaterDisabled, setSaveForLaterLoading }, ref) => {
    const [searchedCompaniesCount, setSearchedCompaniesCount] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const developer = getCookie('devTimeSaver');
    const navigate = useNavigate();
    const { trackEvent } = useAmplitude();
    const { handleErrorTracking } = useHandleSubmitError();
    const { introducerId, checkIntroducerPermission } = useIntroducerData();
    const {
      updateSignupData,
      existingApplicationDataLoaded,
      applicationData,
      stateData,
      stateKey,
    } = useSignupDataDE();

    const methods = useForm<TSignupFormDataDE>({
      resolver: yupResolver(schemaDE),
    });

    const onSubmit = async (values: TSignupFormDataDE) => {
      if (!introducerId) {
        return;
      }
      setIsSubmitting(true);
      setSubmitError(null);

      try {
        const { applicationId } = await updateSignupData(values);
        trackEvent(AMPLITUDE_EVENTS.newApplicationSubmitted, {
          'State key': stateKey!,
          searched_companies_count: searchedCompaniesCount,
          manually_entered_company: !values.company.crefoId,
        });
        IwQueryClient.getOrCreate().invalidateQueries({
          queryKey: ['offers'],
        });
        navigate(PATH_NAMES.newApplicationDocuments({ applicationId }));
      } catch (e: any) {
        setSubmitError(e.message);
      }
      setIsSubmitting(false);
    };

    const onError = (error: FormState<TSignupFormDataDE>['errors']) => {
      handleErrorTracking(error, stateKey);
    };

    useEffect(() => {
      if (existingApplicationDataLoaded) {
        methods.reset({ ...stateData, ...applicationData });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [existingApplicationDataLoaded]);

    const saveForLaterDisabled =
      !methods.watch('company.crefoId') && !methods.watch('company.name');

    useEffect(() => {
      setSaveForLaterDisabled?.(saveForLaterDisabled);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveForLaterDisabled]);

    useImperativeHandle(
      ref,
      () => {
        return {
          saveFormData: async () => {
            setSaveForLaterLoading?.(true);
            await updateSignupData(methods.getValues(), true);
            setSaveForLaterLoading?.(false);
            navigate(PATH_NAMES.home());
          },
        };
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [methods, updateSignupData],
    );

    return (
      <FormProvider {...methods}>
        <div className={styles.container}>
          <form
            className={styles.form}
            onSubmit={methods.handleSubmit(onSubmit, onError)}
          >
            <CompanyDetailsDE
              searchedCompaniesCount={searchedCompaniesCount}
              setSearchedCompaniesCount={setSearchedCompaniesCount}
            />
            <PersonalInformationDE />
            <LoanDetailsDE />
            <hr className={styles.seperator} />
            <TermsAndConditions />

            {submitError && (
              <Message
                type="error"
                message={submitError}
                className={styles.submitError}
              />
            )}

            <div className={styles.buttonContainer}>
              <Button
                variant="primary"
                size="large"
                type="submit"
                loading={isSubmitting}
                disabled={!checkIntroducerPermission('can_create_applications')}
              >
                <Trans>Submit application</Trans>
              </Button>
            </div>
          </form>
        </div>
        {developer && <DevTimeSaver />}
      </FormProvider>
    );
  },
);

NewSignupFormDE.displayName = 'NewSignupFormDE';
